/**
 * Tiny fetch wrapper used by every TanStack Query hook.
 *
 * - Sends cookies with every request (credentials: 'include')
 * - Also sends a Bearer token from localStorage if present (more reliable
 *   across browsers and CDNs than HttpOnly cookies)
 * - Throws ApiError on non-2xx so React Query treats it as a failure
 * - Sets Content-Type for JSON bodies
 */
import type { ApiError } from '@ope-v3/shared';

const API_BASE = '/api';
const TOKEN_KEY = 'ope_token';

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: ApiError,
  ) {
    super(body.message ?? body.error ?? 'API error');
    this.name = 'ApiRequestError';
  }
}

interface RequestInitWithJson extends Omit<RequestInit, 'body'> {
  json?: unknown;
}

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    // localStorage unavailable (private browsing in some browsers)
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

export async function api<T>(path: string, init: RequestInitWithJson = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const finalHeaders = new Headers(headers);
  let body: BodyInit | undefined;
  if (json !== undefined) {
    body = JSON.stringify(json);
    if (!finalHeaders.has('Content-Type')) {
      finalHeaders.set('Content-Type', 'application/json');
    }
  }

  // Attach Bearer token if present
  const token = getToken();
  if (token && !finalHeaders.has('Authorization')) {
    finalHeaders.set('Authorization', `Bearer ${token}`);
  }

  const res = await fetch(`${API_BASE}${path}`, {
    ...rest,
    body,
    headers: finalHeaders,
    credentials: 'include',
  });

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('Content-Type') ?? '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    throw new ApiRequestError(
      res.status,
      typeof data === 'object' && data ? (data as ApiError) : { error: 'Error', message: String(data) },
    );
  }
  return data as T;
}

export const apiGet = <T>(path: string) => api<T>(path);
export const apiPost = <T>(path: string, json?: unknown) => api<T>(path, { method: 'POST', json });
export const apiPut = <T>(path: string, json?: unknown) => api<T>(path, { method: 'PUT', json });
export const apiDelete = <T>(path: string) => api<T>(path, { method: 'DELETE' });
